import { useState, useEffect } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Lockup } from "../components/Lockup";
import { PasswordField } from "../components/PasswordField";
import { dateLong } from "../lib/ct";

// R1 sign-up: the invite-acceptance surface (build brief 8 Sep). One route, four renders decided
// ENTIRELY by the token — accept / expired / used / not_found. The ACCEPT state asks for exactly one
// thing, a password; name/email/seat come from the invitation and render read-only. NO mobile-number
// field (RULING-R1-CAPTURES-NO-MOBILE-NUMBER). Terms acceptance is by action, not a checkbox.
function deviceId() {
  const k = "juno_device_id";
  let v = localStorage.getItem(k);
  if (!v) { v = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())); localStorage.setItem(k, v); }
  return v;
}

const SUPPORT = "mailto:support@junocre.com";

function Chrome({ children }) {
  return (
    <div className="inv" data-testid="accept-page">
      <div className="inv-inner">
        <header className="inv-top">
          <div className="inv-lockup" data-testid="inv-lockup"><Lockup size="pre-account" /></div>
          <span className="inv-release">● PRIVATE RELEASE</span>
        </header>
        {children}
        <footer className="inv-foot">
          <span>© 2026 JUNO CRE LLC · HOUSTON, TEXAS</span>
          <span>JUNO SERVES US PERSONS AND ENTITIES ONLY</span>
        </footer>
      </div>
    </div>
  );
}

const STEPS = [
  ["File your first building", "An address, and whatever paperwork you already have — an offering memo, a rent roll, a T-12. Drag them in as they are."],
  ["The Librarian files it", "It reads every page, files the building and tells you plainly what it could not find."],
  ["The Underwriter marks it", "Usually within the hour: a value with every number sourced back to the page it came from — or an honest no, naming the one document that would change the answer."],
];

function WhatHappensNext() {
  return (
    <aside className="inv-panel" data-testid="inv-what-next">
      <p className="inv-eyebrow">— WHAT HAPPENS NEXT</p>
      <ol className="inv-steps">
        {STEPS.map(([h, b], i) => (
          <li key={i} className="inv-step">
            <span className="inv-step-n">{String(i + 1).padStart(2, "0")}</span>
            <div><p className="inv-step-h">{h}</p><p className="inv-step-b">{b}</p></div>
          </li>
        ))}
      </ol>
      <div className="inv-panel-rule" />
      <p className="inv-panel-lead">The agents do not wait to be asked.</p>
      <p className="inv-panel-body">They start the hour a building lands and keep going while nobody is
        logged in. You are called in to decide, not to operate.</p>
    </aside>
  );
}

function AcceptState({ data, token }) {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [password, setPassword] = useState("");
  const [pwHide, setPwHide] = useState(0);   // bumped on submit: the field goes back to hidden
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const ok = password.length >= 12;
  const firstName = (data.name || "").split(" ")[0] || "there";

  async function submit(e) {
    e.preventDefault();
    setPwHide((n) => n + 1);   // never leave a password revealed behind a submit
    setErr("");
    if (!ok || busy) return;
    setBusy(true);
    try {
      const r = await apiFetch("/identity/signup/invite/accept", {
        method: "POST", body: { token, password, device_id: deviceId() } });
      if (r && r.access) { setSession(r); navigate("/desk", { replace: true }); }
      else navigate("/", { replace: true });
    } catch (e) { setErr(e.detail || String(e.message || e)); setBusy(false); }
  }

  return (
    <div className="inv-accept" data-testid="invite-state-accept">
      <div className="inv-main">
        <p className="inv-eyebrow">— INVITATION · {(data.seat || "owner").toUpperCase()} SEAT</p>
        <h1 className="inv-h1">Get to <span className="inv-yes">yes</span>.<br />Or an <span className="inv-no">honest no</span>.</h1>
        <p className="inv-intro">You have been invited to Juno — a desk of AI agents that works your CRE
          deal to a decision in hours, not months.</p>
        <p className="inv-sub">Set a password and the desk opens. Juno is free while it is in private
          release — no card, no plan to choose.</p>

        <dl className="inv-card" data-testid="inv-details">
          {data.name && (<div className="inv-card-row"><dt>NAME</dt><dd data-testid="inv-name">{data.name}</dd></div>)}
          <div className="inv-card-row"><dt>EMAIL</dt><dd data-testid="inv-email">{data.email}</dd></div>
          <div className="inv-card-row"><dt>SEAT</dt><dd data-testid="inv-seat">{(data.seat || "owner").replace(/^\w/, (c) => c.toUpperCase())}</dd></div>
        </dl>
        <p className="inv-fineprint">Your invitation carries these, so there is nothing to fill in. Not {firstName}?{" "}
          <a href={SUPPORT} className="inv-inline-link" data-testid="inv-not-you">Tell us</a> rather than setting a password.</p>

        <form className="inv-form" onSubmit={submit} data-testid="accept-form">
          <div className="inv-pass-label">
            <label htmlFor="inv-pass">Set a password</label>
          </div>
          <PasswordField id="inv-pass" className="inv-input" value={password} hideOn={pwHide}
                 minLength={12} required autoFocus autoComplete="new-password"
                 placeholder="At least 12 characters" testid="accept-password"
                 onChange={(e) => setPassword(e.target.value)} />
          <p className={`inv-rule ${ok ? "inv-rule-ok" : ""}`} data-testid="inv-rule">
            <span className="inv-dot" />At least 12 characters</p>
          <p className="inv-helper">Length beats complexity. A phrase you can remember is stronger than
            something short with symbols in it.</p>
          {err && <div className="gk-err" data-testid="accept-error">{err}</div>}
          <button className="inv-submit" disabled={!ok || busy} data-testid="accept-submit-btn">
            {busy ? "Creating your account…" : "Create account and open the desk"}
          </button>
          <p className="inv-terms">Setting a password creates your account and accepts the{" "}
            <a href="/terms">Terms of service</a> and <a href="/privacy">Privacy policy</a>.</p>
        </form>

        <div className="inv-expiry-rule" />
        {data.expires_at && (
          <p className="inv-expiry">THIS INVITATION EXPIRES {dateLong(data.expires_at).toUpperCase()}</p>
        )}
      </div>
      <WhatHappensNext />
    </div>
  );
}

function ExpiredState({ email, token }) {
  const [requested, setRequested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  async function request() {
    if (busy) return;
    setBusy(true); setErr("");
    try {
      await apiFetch("/identity/invite/resend-request", { method: "POST", body: { token } });
      setRequested(true);
    } catch (e) { setErr(e.detail || "Could not request a new invitation."); }
    setBusy(false);
  }

  return (
    <div className="inv-single" data-testid="invite-state-expired">
      <p className="inv-eyebrow">— INVITATION · EXPIRED</p>
      <h1 className="inv-h1">This invitation<br />has run out.</h1>
      <p className="inv-body">Invitations last seven days. Nothing is wrong with your account — one has
        not been created yet. Ask for a fresh link and it will be with you shortly.</p>
      {requested ? (
        <div className="inv-asked" data-testid="inv-requested-panel">
          <p className="inv-asked-tag">ASKED FOR</p>
          <p className="inv-asked-body">A new invitation has been requested{email ? ` for ${email}` : ""}.
            Someone at Juno will send it shortly — there is nothing more to do.</p>
        </div>
      ) : (
        <button className="inv-submit inv-submit-inline" onClick={request} disabled={busy}
                data-testid="inv-request-new-btn">
          {busy ? "Requesting…" : "Request a new invitation"}
        </button>
      )}
      {err && <div className="gk-err" data-testid="inv-expired-error">{err}</div>}
      {email && <p className="inv-note">Sent to {email} — the address this invitation was issued to.</p>}
    </div>
  );
}

function UsedState() {
  return (
    <div className="inv-single" data-testid="invite-state-used">
      <p className="inv-eyebrow">— INVITATION · USED</p>
      <h1 className="inv-h1">You are<br />already in.</h1>
      <p className="inv-body">This link created your account, so it will not open a second time. Sign in
        with your password to reach the desk.</p>
      <Link to="/desk" className="inv-submit inv-submit-inline" data-testid="inv-signin-btn">Sign in</Link>
      <p className="inv-note">Forgotten your password? You can reset it by email from the sign-in
        screen — it takes a minute.</p>
    </div>
  );
}

// The NEUTRAL state (founder ruling 11 Oct 2026). A WITHDRAWN invitation and a link we cannot find
// render the SAME page and are indistinguishable — the URL is a bearer token, so anyone holding it
// (forwarded, found, guessed) must learn nothing about whether an address was invited or what we did
// with it. EXPIRED keeps its own page (that page is what sets `resend_requested` and makes variant B
// possible), and USED keeps its sign-in prompt; neither is folded in here.
function NotFoundState() {
  return (
    <div className="inv-single" data-testid="invite-state-not_found">
      <p className="inv-eyebrow">— INVITATION</p>
      <h1 className="inv-h1">This invitation<br />is no longer active.</h1>
      <p className="inv-body">If you think that's a mistake, email <a href={SUPPORT}
        className="inv-inline-link" data-testid="inv-support-link">support@junocre.com</a> and a person
        will sort it out.</p>
      <p className="inv-note">Email programs sometimes cut a link in half, so it is worth pasting the
        whole address into your browser first — including any part that wrapped onto a second line.</p>
    </div>
  );
}

export default function AcceptInvite() {
  const params = useParams();
  const [search] = useSearchParams();
  const [token] = useState(() => params.token || search.get("token") || "");
  const [view, setView] = useState(null); // {state, ...}

  // strip a query-string token from the visible URL/history (OWASP: no referrer leak). Path-token
  // (/invite/:token) is left in place — the accept surface's NOT FOUND state exists precisely so a
  // clipped path link fails honestly.
  useEffect(() => {
    if (search.get("token") && window.location.search) {
      window.history.replaceState({}, document.title, "/accept");
    }
  }, [search]);

  useEffect(() => {
    if (!token) { setView({ state: "not_found" }); return; }
    apiFetch(`/identity/invite/lookup/${encodeURIComponent(token)}`)
      .then(setView).catch(() => setView({ state: "not_found" }));
  }, [token]);

  if (!view) return <Chrome><div className="inv-single" data-testid="invite-loading">
    <p className="inv-body">Checking your invitation…</p></div></Chrome>;

  return (
    <Chrome>
      {view.state === "accept" ? <AcceptState data={view} token={token} />
        : view.state === "expired" ? <ExpiredState email={view.email} token={token} />
        : view.state === "used" ? <UsedState />
        : <NotFoundState />}
    </Chrome>
  );
}
